/**
 * Состав выборки одной 100%-полосой — ОДНИМ компонентом на всё приложение.
 *
 * До него каждый экран рисовал `by_state` / `by_status` своей двухколоночной
 * таблицей, и одинаковые по смыслу разрезы читались как разные величины:
 * где-то знаменатель назван, где-то нет, где-то часть ключей молча выпадала
 * из показа. Здесь это закрыто формой, а не договорённостью:
 *
 * * **знаменатель назван вслух.** Полоса без «из чего сто процентов» — это
 *   доля неизвестно от чего; поэтому `total` и `denominator` обязательны;
 * * **легенда видна всегда.** Она же — место, где стоят сами числа: сегмент
 *   тоньше волоса всё равно прочитан, потому что его величина не в полосе;
 * * **цвет не единственный носитель смысла.** У каждого сегмента своя
 *   штриховка и своя подпись; в чёрно-белой печати и при дальтонизме полоса
 *   остаётся читаемой. Сам цвет полосы означает только порядок сегментов:
 *   смысловой цвет ключа (карантинный красный) остаётся на своей отметке
 *   рядом с подписью и в полосу не переносится;
 * * **сумма сегментов сверяется со знаменателем.** Недобор показан отдельным
 *   сегментом «не отнесено», перебор — названным расхождением. Молча
 *   растянуть сегменты до ста процентов значило бы спрятать дефект данных;
 * * **эпистемический класс один на весь набор.** Поля «класс сегмента» здесь
 *   физически нет: складывать в одну полосу официальное с синтетическим
 *   нельзя, и компонент не даёт этого выразить.
 */

import type { ReactNode } from "react";
import { Disclosure } from "./Disclosure";
import { StatusMark, type DataStatus } from "./StatusMark";
import { EvidenceBadge } from "./ui";
import { formatCount, plural } from "../lib/format";
import { share, type Share } from "../lib/progress";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";
import type { CountNoun } from "./Progress";
import styles from "./stackedshare.module.css";

const m = defineMessages({
  noDenominator: { id: "common.kit.share.noDenominator", defaultMessage: "нет знаменателя", description: "Вместо процента в легенде полосы состава: делить не на что; строчная, коротко" },
  lessThanOne: { id: "common.kit.share.lessThanOne", defaultMessage: "менее 1%", description: "Вместо «0%» для крошечной, но не нулевой доли сегмента; коротко" },
  total: { id: "common.kit.share.total", defaultMessage: "{count} {noun} — {denominator}", description: "Шапка полосы состава: сколько всего единиц и от чего считаем; {count} — число, {noun} — что считаем в нужной форме, {denominator} — знаменатель словами («в кампании»)" },
  absent: { id: "common.kit.share.absent", defaultMessage: "Считать не от чего: в этом знаменателе нет ни одной единицы.", description: "Подпись под пустой полосой состава: знаменатель равен нулю" },
  thin: { id: "common.kit.share.thin", defaultMessage: "тоньше полосы — величина читается здесь", description: "Пометка у сегмента легенды: на полосе его почти не видно, число — здесь" },
  unassigned: { id: "common.kit.share.unassigned", defaultMessage: "не отнесено", description: "Сегмент полосы состава: единицы, не попавшие ни в один сегмент; строчная, коротко" },
  unassignedNote: { id: "common.kit.share.unassignedNote", defaultMessage: "Знаменатель больше суммы разреза: эти единицы ни в один сегмент не попали.", description: "Пояснение к сегменту «не отнесено»" },
  overflow: { id: "common.kit.share.overflow", defaultMessage: "Сумма сегментов ({counted}) больше знаменателя ({total}) на {overflow} — расхождение источника, а не округление показа.", description: "Предупреждение под полосой состава: сумма сегментов больше целого; все три аргумента — числа, уже отформатированные" },
  tableTitle: { id: "common.kit.share.tableTitle", defaultMessage: "Таблица разреза", description: "Заголовок раскрывающейся таблицы под полосой состава" },
  tableSummary: { id: "common.kit.share.tableSummary", defaultMessage: "{count, plural, one {# сегмент} few {# сегмента} many {# сегментов} other {# сегмента}}", description: "Краткое содержание таблицы под полосой состава: сколько в ней сегментов" },
  tableMore: { id: "common.kit.share.tableMore", defaultMessage: "показать числа таблицей", description: "Приписка-ссылка раскрывающегося блока: показать те же числа таблицей" },
  segmentHeader: { id: "common.kit.share.segmentHeader", defaultMessage: "Сегмент", description: "Заголовок первой колонки таблицы состава" },
  shareHeader: { id: "common.kit.share.shareHeader", defaultMessage: "Доля", description: "Заголовок колонки таблицы состава: доля сегмента; узкая колонка" },
});

export interface ShareSegment {
  /** Ключ источника ровно как он пришёл. */
  key: string;
  /** Подпись для человека. */
  label: string;
  count: number;
  /**
   * Собственная отметка сегмента экрана — например карантинная точка статуса.
   * Цвет полосы говорит про ПОРЯДОК разреза, а не про смысл ключа: смысловой
   * цвет (красный/жёлтый/зелёный) живёт на своей отметке рядом с подписью, там
   * же, где и на остальных экранах.
   */
  mark?: ReactNode;
  /**
   * Состояние из семантической палитры данных (ADR-0009). Отметка рисуется
   * `StatusMark`, то есть иконкой и подписью, а не одним цветом — и рисуется
   * РЯДОМ с подписью, а не заливкой полосы: заливка занята порядком.
   */
  status?: DataStatus;
  /** Короткое пояснение: чем этот сегмент не то, чем кажется. */
  note?: ReactNode;
}

/** Порядок штриховок. Индекс сегмента → внешний вид, один и тот же в легенде. */
const PATTERNS = [
  styles.seg1,
  styles.seg2,
  styles.seg3,
  styles.seg4,
  styles.seg5,
] as const;

const patternOf = (index: number) => PATTERNS[index % PATTERNS.length] ?? "";

/**
 * Отметка сегмента: сначала состояние (иконка + подпись в доступном имени),
 * затем собственная отметка экрана. Цветной точки без подписи здесь быть не
 * может — смысловой цвет один не носит.
 */
function SegmentMark({ segment }: { segment: ShareSegment }) {
  if (!segment.status && !segment.mark) return null;
  return (
    <span className={styles.legendMark}>
      {segment.status ? (
        <StatusMark status={segment.status} label={segment.label} compact />
      ) : null}
      {segment.mark}
    </span>
  );
}

/** Доля, которую на полосе уже не разглядеть: величина живёт только в легенде. */
const THIN = 0.02;

function percentText(value: Share): string {
  if (value.percent === null || value.value === null) return tr(m.noDenominator);
  if (value.percent === 0 && value.value > 0) return tr(m.lessThanOne);
  return `${value.percent}%`;
}

export function StackedShare({
  title,
  segments,
  total,
  denominator,
  noun,
  caption,
  epistemic,
  absent,
}: {
  /** Что за разрез. */
  title: ReactNode;
  /** Сегменты в осмысленном порядке источника, а не по величине. */
  segments: ReadonlyArray<ShareSegment>;
  /** Знаменатель числом. Сто процентов — это он. */
  total: number;
  /** Знаменатель словами: «обязательств кампании», «контуров реестра». */
  denominator: string;
  /** В чём считаем. */
  noun: CountNoun;
  /** Смысл разреза. Подпись обязательна: полоса без неё — украшение. */
  caption: ReactNode;
  /** Класс доказательства ВСЕГО набора. Смешивать классы в полосе нельзя. */
  epistemic?: string | null;
  /** Почему считать не от чего. Показывается вместо полосы при пустом знаменателе. */
  absent?: ReactNode;
}) {
  const t = useT();
  const counted = segments.reduce((sum, segment) => sum + segment.count, 0);
  // Недобор — это «не отнесено», а не повод растянуть остальные сегменты.
  const unassigned = total - counted;
  const overflow = counted - total;
  const base = Math.max(counted, total, 1);

  const head = (
    <div className={styles.head}>
      <span className={styles.title}>{title}</span>
      <span className={styles.total}>
        {t(m.total, { count: formatCount(total).text, noun: plural(total, noun), denominator })}
      </span>
      {epistemic ? <EvidenceBadge status={epistemic} /> : null}
    </div>
  );

  if (total <= 0) {
    return (
      <div className={styles.block}>
        {head}
        {/* Пустой знаменатель не рисуется нулём: пустая полоса читалась бы как
            «ноль процентов», то есть как провал, а не как отсутствие предмета
            счёта. */}
        <div className={`${styles.bar} ${styles.barMissing}`} />
        <p className={styles.caption}>
          {absent ?? t(m.absent)}
        </p>
      </div>
    );
  }

  const rows = segments.map((segment, index) => ({
    segment,
    pattern: patternOf(index),
    part: share(segment.count, total),
    width: (segment.count / base) * 100,
  }));

  return (
    <div className={styles.block}>
      {head}

      <div className={styles.bar}>
        {rows.map((row) => (
          <div
            key={row.segment.key}
            className={`${styles.seg} ${row.pattern}`}
            style={{ width: `${row.width}%` }}
            aria-hidden
          />
        ))}
        {unassigned > 0 ? (
          <div
            className={`${styles.seg} ${styles.segUnassigned}`}
            style={{ width: `${(unassigned / base) * 100}%` }}
            aria-hidden
          />
        ) : null}
      </div>

      {/* Легенда — не расшифровка цвета, а место, где стоят числа. Поэтому она
          видна всегда и не уезжает в раскрытие. */}
      <ul className={styles.legend}>
        {rows.map((row) => (
          <li key={row.segment.key} className={styles.legendItem}>
            <span className={`${styles.sample} ${row.pattern}`} aria-hidden />
            <span className={styles.legendText}>
              <span className={styles.legendLabel}>
                <SegmentMark segment={row.segment} />
                {row.segment.label}
              </span>
              <span className={styles.legendValue}>
                {formatCount(row.segment.count).text} · {percentText(row.part)}
              </span>
              {row.part.value !== null && row.part.value > 0 && row.part.value < THIN ? (
                <span className={styles.legendNote}>{t(m.thin)}</span>
              ) : null}
              {row.segment.note ? (
                <span className={styles.legendNote}>{row.segment.note}</span>
              ) : null}
            </span>
          </li>
        ))}
        {unassigned > 0 ? (
          <li className={styles.legendItem}>
            <span className={`${styles.sample} ${styles.segUnassigned}`} aria-hidden />
            <span className={styles.legendText}>
              <span className={styles.legendLabel}>{t(m.unassigned)}</span>
              <span className={styles.legendValue}>
                {formatCount(unassigned).text} ·{" "}
                {percentText(share(unassigned, total))}
              </span>
              <span className={styles.legendNote}>{t(m.unassignedNote)}</span>
            </span>
          </li>
        ) : null}
      </ul>

      <p className={styles.caption}>{caption}</p>

      {overflow > 0 ? (
        <p className={styles.caption}>
          {t(m.overflow, {
            counted: formatCount(counted).text,
            total: formatCount(total).text,
            overflow: formatCount(overflow).text,
          })}
        </p>
      ) : null}

      <Disclosure
        tone="inline"
        title={t(m.tableTitle)}
        summary={t(m.tableSummary, { count: segments.length })}
        more={t(m.tableMore)}
      >
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t(m.segmentHeader)}</th>
              <th scope="col" className={styles.num}>
                {denominator}
              </th>
              <th scope="col" className={styles.num}>
                {t(m.shareHeader)}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.segment.key}>
                <th scope="row">
                  <SegmentMark segment={row.segment} />
                  {row.segment.label}
                  {row.segment.note ? (
                    <span className={styles.legendNote}>{row.segment.note}</span>
                  ) : null}
                </th>
                <td className={styles.num}>{formatCount(row.segment.count).text}</td>
                <td className={styles.num}>{percentText(row.part)}</td>
              </tr>
            ))}
            {unassigned > 0 ? (
              <tr>
                <th scope="row">{t(m.unassigned)}</th>
                <td className={styles.num}>{formatCount(unassigned).text}</td>
                <td className={styles.num}>{percentText(share(unassigned, total))}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Disclosure>
    </div>
  );
}
